import _ from 'lodash';

const { startCase } = _;

/**
 * A stable, globally unique identifier for a tool implementation.
 *
 * Format: `<provider>/<action>[/<sub-action>]`
 *   - Provider segment: lowercase, hyphenated (e.g., `google-keyword`, `sprout-social`)
 *   - Action segments: camelCase or hyphenated (e.g., `sendMessage`, `email-finder`)
 *   - Separated by `/`
 *
 * Examples:
 *   - `hunter/email-finder`
 *   - `slack/messaging/sendMessage`
 *   - `sprout-social/listening/metrics`
 *   - `coco/sub-agent` (the `coco/` prefix is reserved for Cotera-builtin tools)
 *
 * Tool keys are used to:
 *   - Link tool implementations to database records (`tools_v2.name`)
 *   - Register tools in the BuiltinToolLoader registry
 *   - Reference tools in `tool_instances`
 *   - Store tool execution history and analytics
 *
 * WARNING: Renaming a tool key is a breaking change. It will orphan existing
 * database records and break any agents or workflows that reference the tool.
 */
export class ToolKey {
  constructor(public readonly key: string) {}

  static fromString(key: string): ToolKey {
    return new ToolKey(key);
  }

  /**
   * Creates a ToolKey from an LLM name (underscore-delimited format).
   *
   * This is a lossy inverse of `toLlmName()`: it replaces all underscores with
   * slashes. It does NOT reconstruct camelCase, since that would require knowing
   * which underscores were path separators vs. word boundaries.
   *
   * As a result, `fromLlmName(key.toLlmName())` only round-trips correctly when
   * the original key has no camelCase segments. Use `defensiveEqual()` for
   * matching LLM-provided names back to tool keys.
   */
  static fromLlmName(name: string): ToolKey {
    return new ToolKey(name.replaceAll('_', '/'));
  }

  /**
   * Converts the tool key to an LLM-compatible name.
   * Replaces slashes with underscores, preserving camelCase.
   *
   * Example: "linkedin/profile/getWithPosts" -> "linkedin_profile_getWithPosts"
   */
  toLlmName(): string {
    return this.key.replaceAll('/', '_');
  }

  /**
   * Human-readable display name derived from the last segment of the key.
   * e.g., "hunter/email-finder" -> "Email Finder"
   */
  displayName(): string {
    return startCase(this.key.split('/').pop()!);
  }

  /**
   * Converts a string to a normalized snake_case format for comparison.
   * Used internally for defensive matching.
   */
  private static toNormalizedSnakeCase(str: string): string {
    return str
      .replaceAll('/', '_')
      .replace(/([a-z])([A-Z])/g, '$1_$2')
      .toLowerCase();
  }

  toString(): string {
    return this.key;
  }

  /**
   * Get the provider from the tool key (first segment).
   * e.g., "devin/sessions/create" -> "devin"
   */
  get provider(): string {
    return this.key.split('/')[0]!;
  }

  /**
   * Returns true if the key is equal to the other key, or if the other key is a valid LLM name that matches the key.
   * Handles both the original key format, the underscore LLM format, and snake_case variations
   * (some LLM providers like Claude normalize function names to snake_case).
   */
  defensiveEqual(other: string): boolean {
    // Direct matches
    if (this.key === other || this.toLlmName() === other) {
      return true;
    }
    // Normalized comparison to handle LLM providers that convert camelCase to snake_case
    return (
      ToolKey.toNormalizedSnakeCase(this.key) ===
      ToolKey.toNormalizedSnakeCase(other)
    );
  }

  /**
   * Exact string match. Use this when comparing against known canonical keys
   * (e.g., database lookups, registry keys). Use `defensiveEqual()` when
   * matching against names that may have been transformed by an LLM.
   */
  strictEqual(other: string): boolean {
    return this.key === other;
  }
}
